import { useState } from 'react';
import {
  FileText,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  Clock,
  Flame,
  Thermometer,
  Snowflake,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Call } from '../types';
import { formatDateTime, formatDuration, getPhaseLabel } from '../utils/format';
import { api } from '../api/client';

interface SEEDocumentsProps {
  calls: Call[];
  loading: boolean;
  onRefresh: () => void;
  onCallClick: (call: Call) => void;
}

export default function SEEDocuments({
  calls,
  loading,
  onRefresh,
  onCallClick,
}: SEEDocumentsProps) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filter calls with evaluations
  const documentsWithEval = calls.filter((c) => c.evaluation);

  const handleApprove = async (e: React.MouseEvent, call: Call) => {
    e.stopPropagation();
    setActionLoading(call.id);
    try {
      await api.approveEvaluation(call.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSync = async (e: React.MouseEvent, call: Call) => {
    e.stopPropagation();
    setActionLoading(call.id);
    try {
      await api.syncCall(call.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to sync:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getLeadScoreIcon = (score: number) => {
    if (score > 25) return <Flame className="w-4 h-4 text-red-500" />;
    if (score >= 0) return <Thermometer className="w-4 h-4 text-yellow-500" />;
    return <Snowflake className="w-4 h-4 text-blue-500" />;
  };

  if (loading && documentsWithEval.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Lade SEE Dokumente...</p>
        </div>
      </div>
    );
  }

  if (documentsWithEval.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Keine SEE Dokumente
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Keine SEE Dokumente zur Uberprufung vorhanden.
          </p>
          <button
            onClick={onRefresh}
            className="mt-4 btn-secondary"
            disabled={loading}
          >
            <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Aktualisieren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">SEE Dokumente</h2>
          <p className="text-sm text-gray-500">
            {documentsWithEval.length} Dokumente verfugbar
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-secondary"
          disabled={loading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
          Aktualisieren
        </button>
      </div>

      {/* Document Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentsWithEval.map((call) => {
          const evaluation = call.evaluation!;

          return (
            <div
              key={call.id}
              className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onCallClick(call)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {call.company_name || call.contact_name || 'Unbekannt'}
                  </h3>
                  <div className="flex items-center space-x-1 text-xs text-gray-500 mt-0.5">
                    <Clock className="w-3 h-3" />
                    <span>{formatDateTime(call.started_at)}</span>
                    <span>•</span>
                    <span>{formatDuration(call.duration_seconds)}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  {getLeadScoreIcon(evaluation.lead_score)}
                  <span className="text-sm font-bold">{evaluation.lead_score}</span>
                </div>
              </div>

              {/* Summary */}
              <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                {evaluation.summary}
              </p>

              {/* Phase & Status */}
              <div className="flex items-center justify-between mb-3">
                <span className="badge badge-blue">
                  {getPhaseLabel(evaluation.phase)}
                </span>
                <div className="flex items-center space-x-2">
                  {evaluation.is_approved && (
                    <span className="badge badge-green">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Freigegeben
                    </span>
                  )}
                  {call.synced_to_hubspot_at && (
                    <span className="badge badge-green">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Synced
                    </span>
                  )}
                </div>
              </div>

              {/* Tags */}
              {evaluation.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {evaluation.tags.slice(0, 3).map((tag, i) => (
                    <span key={i} className="badge badge-gray text-xs">
                      {tag}
                    </span>
                  ))}
                  {evaluation.tags.length > 3 && (
                    <span className="badge badge-gray text-xs">
                      +{evaluation.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center space-x-2 pt-3 border-t border-gray-100">
                {!evaluation.is_approved && (
                  <button
                    onClick={(e) => handleApprove(e, call)}
                    disabled={actionLoading === call.id}
                    className="flex-1 btn-primary text-xs py-1.5"
                  >
                    {actionLoading === call.id ? (
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3 h-3 mr-1" />
                    )}
                    Freigeben
                  </button>
                )}
                {evaluation.is_approved && !call.synced_to_hubspot_at && (
                  <button
                    onClick={(e) => handleSync(e, call)}
                    disabled={actionLoading === call.id}
                    className="flex-1 btn-primary text-xs py-1.5"
                  >
                    {actionLoading === call.id ? (
                      <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <ExternalLink className="w-3 h-3 mr-1" />
                    )}
                    Sync to HubSpot
                  </button>
                )}
                {!evaluation.is_approved && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Reject not implemented
                    }}
                    className="flex-1 btn-secondary text-xs py-1.5"
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Ablehnen
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
